import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { getCurrencySymbol } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';

/**
 * `useGrouping` jest tu konieczne: polskie locale grupuje domyślnie dopiero od
 * pięciu cyfr, więc bez tego kwota rzędu tysięcy wychodzi jako `3400,00`
 * zamiast `3 400,00`. Separatorem jest wąska spacja nierozdzielająca.
 */
const priceFormat = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: true,
});

export function formatDecimal(amount: number): string {
  return priceFormat.format(amount);
}

export function formatPrice(amount: number, currency: string): string {
  return `${priceFormat.format(amount)} ${getCurrencySymbol(currency)}`;
}

export const formatPriceList = (prices: MoneyValueDto[]): string =>
  prices.map((p) => formatPrice(p.amount, p.currency)).join(' + ');

/**
 * Liczba bez wymuszonych groszy — kwoty w cennikach i kolumny liczbowe, gdzie
 * „200” niesie tyle samo co „200,00”, a dwa zera to szum.
 */
const numberFormat = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 2,
  useGrouping: true,
});

export function formatNumber(value: number): string {
  return numberFormat.format(value);
}

/**
 * Wartość rozbita na część główną i drugorzędną. Podział robimy raz tutaj, bo
 * ten sam wzór („to czytamy, to tylko dopowiada”) obsługuje kwoty, daty
 * i lokalizacje.
 */
export interface DisplayParts {
  main: string;
  /** Doklejane bez odstępu — własne spacje wchodzą do środka napisu. */
  secondary: string;
}

/**
 * Kwota jako `12 231` + `,00 zł`: złotówki tworzą pion, grosze i waluta
 * schodzą na drugi plan.
 */
export function splitPrice(amount: number, currency: string): DisplayParts {
  const formatted = priceFormat.format(amount);
  const fractionIndex = formatted.lastIndexOf(',');
  const symbol = getCurrencySymbol(currency);

  if (fractionIndex === -1) {
    return { main: formatted, secondary: ` ${symbol}` };
  }
  return {
    main: formatted.slice(0, fractionIndex),
    secondary: `${formatted.slice(fractionIndex)} ${symbol}`,
  };
}

/** Kwota bez groszy plus sama waluta — cenniki wyjazdów („200–300 zł”). */
export function splitAmountWithCurrency(
  amount: number,
  currency: string,
): DisplayParts {
  return {
    main: formatNumber(amount),
    secondary: ` ${getCurrencySymbol(currency)}`,
  };
}
